// Sugalarm — Cycle 107: reusable labeled action sheet.
//
// A small menu of labeled options (glyph + text), with an optional muted
// caption at the top. Generalizes the cycle-106 camera chooser so the
// Members rows anchor each action to the object it affects (the avatar's
// photo sheet, the connection pill's CGM sheet). One compact centered
// modal across all variants, reusing the cycle-34.20 scale-in + blurred-
// backdrop pattern. Dismisses on tap-outside, Escape, and after any
// option is chosen. Focus moves into the sheet on open and returns to the
// trigger on close (Tab is trapped within the options).
//
// Options carry an intent tint (default ink / `warn` amber / `urgent`
// red) applied to glyph + label together, and an optional `divider` to
// separate a destructive option from the rest.

import { useEffect, useRef } from "react";
import type { ReactNode } from "react";

import styles from "./ActionSheet.module.css";

export interface ActionSheetOption {
  key: string;
  label: string;
  icon?: ReactNode;
  intent?: "default" | "warn" | "urgent";
  /** Render a thin divider above this option (separates a destructive
   *  action from the rest). */
  divider?: boolean;
  onSelect: () => void;
}

interface Props {
  open: boolean;
  /** Muted caption line at the top (e.g. "the operator's photo"). */
  caption?: string;
  options: ActionSheetOption[];
  onClose: () => void;
}

export function ActionSheet({ open, caption, options, onClose }: Props) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  // Capture the trigger to restore focus to it on close; move focus into
  // the sheet on open.
  useEffect(() => {
    if (!open) return;
    restoreRef.current = document.activeElement as HTMLElement | null;
    const first = panelRef.current?.querySelector<HTMLButtonElement>(
      "button[data-opt]",
    );
    first?.focus();
    return () => {
      restoreRef.current?.focus?.();
    };
  }, [open]);

  // Escape closes; Tab is trapped within the option buttons.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const btns = panelRef.current?.querySelectorAll<HTMLButtonElement>(
          "button[data-opt]",
        );
        if (!btns || btns.length === 0) return;
        const list = Array.from(btns);
        const first = list[0]!;
        const last = list[list.length - 1]!;
        const active = document.activeElement;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const choose = (opt: ActionSheetOption) => {
    onClose();
    opt.onSelect();
  };

  return (
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label={caption ?? "Choose an action"}
      onClick={onClose}
    >
      <div
        ref={panelRef}
        className={styles.panel}
        onClick={(e) => e.stopPropagation()}
      >
        {caption ? <div className={styles.caption}>{caption}</div> : null}
        {options.map((opt) => (
          <button
            key={opt.key}
            type="button"
            data-opt
            className={[
              styles.option,
              opt.intent === "warn" ? styles.optionWarn : "",
              opt.intent === "urgent" ? styles.optionUrgent : "",
              opt.divider ? styles.divided : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() => choose(opt)}
          >
            {opt.icon ? (
              <span className={styles.optIcon} aria-hidden>
                {opt.icon}
              </span>
            ) : null}
            <span className={styles.optLabel}>{opt.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
